import { ClickAwayListener, Divider, MenuList } from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon'
import ListItemText from '@mui/material/ListItemText/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import React from 'react'
import {
  AccountDoc,
  GroupDoc,
  GroupStatus,
  UserSettingsDoc
} from 'types/api/changes'

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'

import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'

import MarkunreadOutlinedIcon from '@mui/icons-material/MarkunreadOutlined'

import { t } from 'i18next'
import {
  isGroupArchived,
  isGroupHidden,
  isGroupMuted,
  isGroupUnread
} from 'utils/api/userSettings'
import { Model } from 'types/model'
import { useCalendar } from 'components/providers/CalendarProvider'
import { isGroupcalApp } from 'utils/appType'
import { performUpdateGroupSettings } from 'components/popups/groupInfo/SettingsForCalendarSection'
import { GroupContextMenuProps } from 'components/sidebar/Groups'
import { isGroupBlockedToAddEvents } from 'utils/api/groups'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'

import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import {
  GroupMenuAllCalendars,
  GroupMenuAllCalendarsOn,
  GroupMenuArchive,
  GroupMenuArchiveOff,
  GroupMenuCalendarDetails,
  GroupMenuDefault,
  GroupMenuReminders,
  GroupMenuRemindersOn,
  GroupMenuUnread,
  GroupMenuUnreadOff
} from 'assets/icons/group-menu'

interface ContextMenuProps {
  group: GroupDoc
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc
  account?: Model<AccountDoc>
  x: number // X-coordinate of the click event
  y: number // Y-coordinate of the click event
  contextMenuPosition: GroupContextMenuProps
  onClose: () => void // Callback to close the context menu
}

const menuItemTextStyle = {
  fontSize: '14px',
  fontFamily: 'NotoSans',
  fontWeight: 400
}

const GroupItemContextMenu: React.FC<ContextMenuProps> = ({
  group,
  userSettings,
  account,
  x,
  y,
  contextMenuPosition,
  onClose
}) => {
  const archiveText = isGroupArchived(userSettings, group._id ?? '')
    ? t('unarchive')
    : t('archive')
  const archiveIcon = isGroupArchived(userSettings, group._id ?? '') ? (
    <GroupMenuArchiveOff />
  ) : (
    <GroupMenuArchive />
  )
  const unreadText = isGroupUnread(userSettings, group._id ?? '')
    ? t('markAsRead')
    : t('markAsUnread')
  const unreadIcon = isGroupUnread(userSettings, group._id ?? '') ? (
    <GroupMenuUnreadOff />
  ) : (
    <GroupMenuUnread />
  )
  const allCalendarsText = isGroupHidden(userSettings, group._id ?? '')
    ? t('showOnAllCal', { cal: t('allCalendarsButton') })
    : t('dontShowOnAllCal', { cal: t('allCalendarsButton') })
  const allCalendarsIcon = isGroupHidden(userSettings, group._id ?? '') ? (
    <GroupMenuAllCalendarsOn />
  ) : (
    <GroupMenuAllCalendars />
  )

  const remindersText = isGroupMuted(userSettings, group._id ?? '')
    ? t('enableNotifications')
    : t('disableNotifications')
  const remindersIcon = isGroupMuted(userSettings, group._id ?? '') ? (
    <GroupMenuRemindersOn />
  ) : (
    <GroupMenuReminders />
  )

  const calendar = useCalendar()

  const showCalendarDetails = () => {
    if (group._id) {
      calendar.onGroupServer(group._id)
      setTimeout(() => {
        calendar.onGroupInfo(group._id)
      }, 50)
    }

    onClose()
  }

  const handleArchiveCalendar = () => {
    // Logic for "Archive calendar" action
    onClose()
    const newValue = isGroupArchived(
      userSettings,
      contextMenuPosition?.group._id ?? ''
    )
      ? GroupStatus.ACTIVE
      : GroupStatus.ARCHIVED
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        GroupStatus: newValue
      }
    ).then(() => {
      calendar.onGroupServer('')
    })
  }

  const handleMarkAsUnread = () => {
    // Logic for "Mark as unread" action
    onClose()
    const newValue = isGroupUnread(
      userSettings,
      contextMenuPosition?.group._id ?? ''
    )
      ? '0'
      : '1'
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        Unread: newValue
      }
    )
  }

  const handleHideFromCalendars = () => {
    // Logic for "Don't show on all calendars" action
    onClose()

    const newValue = isGroupHidden(
      userSettings,
      contextMenuPosition?.group._id ?? ''
    )
      ? '1'
      : '0'
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        ShowOnAllCalendars: newValue
      }
    )
  }

  const makeDefaultCalendar = () => {
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        Default: '1'
      }
    ).then(() => {
      onClose()
    })
  }

  function handleReminderState(): void {
    performUpdateGroupSettings(
      userSettings,
      contextMenuPosition?.group._id ?? '',
      {
        ActivateReminders: isGroupMuted(userSettings, group._id ?? '')
          ? '1'
          : '0'
      }
    ).then(() => {
      onClose()
    })
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={onClose}
    >
      <ul
        style={{
          position: 'absolute',
          width: 'fit-content',
          top: y,
          left: x,
          listStyleType: 'none',
          zIndex: 99999,
          fontSize: '14px',
          padding: '0px 8px',
          borderRadius: '20px',
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
        }}
      >
        <MenuList sx={{ fontSize: '14px' }}>
          <MenuItem
            id="show-group-details-button"
            onClick={showCalendarDetails}
          >
            <ListItemIcon>
              <GroupMenuCalendarDetails />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
              {t('calendarDetails')}
            </ListItemText>
          </MenuItem>
          <Divider />
          {isGroupcalApp() && (
            <MenuItem onClick={handleMarkAsUnread}>
              <ListItemIcon>{unreadIcon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
                {unreadText}
              </ListItemText>
            </MenuItem>
          )}
          {
            <MenuItem
              id="show-on-all-calendar-button"
              onClick={handleHideFromCalendars}
            >
              <ListItemIcon>{allCalendarsIcon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
                {allCalendarsText}
              </ListItemText>
            </MenuItem>
          }

          {
            <MenuItem onClick={handleReminderState}>
              <ListItemIcon>{remindersIcon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
                {remindersText}
              </ListItemText>
            </MenuItem>
          }

          {account &&
          !isGroupBlockedToAddEvents(
            account,
            group,
            userSettings,
            LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
          ) ? (
            <MenuItem
              id="make-default-calendar-button"
              onClick={makeDefaultCalendar}
            >
              <ListItemIcon>
                <GroupMenuDefault />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
                {t('defaultCalendar')}
              </ListItemText>
            </MenuItem>
          ) : (
            <></>
          )}
          <Divider />
          {isGroupcalApp() && (
            <MenuItem id="archive-group-button" onClick={handleArchiveCalendar}>
              <ListItemIcon>{archiveIcon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ sx: menuItemTextStyle }}>
                {archiveText}
              </ListItemText>
            </MenuItem>
          )}
        </MenuList>
      </ul>
    </ClickAwayListener>
  )
}

export default GroupItemContextMenu
